import { createRouter, createWebHashHistory, RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useSuperAdminCreditorAccessStore } from '@/stores/superAdminCreditorAccess';
import { pickFallbackRouteName, type ModuleKey } from '@/permissions';
import AppLayout from '@/layouts/AppLayout.vue';
import AuthLayout from '@/layouts/AuthLayout.vue';
import DashboardView from '@/views/DashboardView.vue';
import PaymentsView from '@/views/payments/PaymentsView.vue';
import AccountsView from '@/views/accounts/AccountsView.vue';
import ReportsView from '@/views/reports/ReportsView.vue';

const publicRouteNames = ['sign-in', 'auto-login', 'remind-password', 'auth-callback'];

const APP_NAME = 'FORTH Creditor Portal';

const appShellChildren: Array<RouteRecordRaw> = [
    {
        path: '',
        name: 'dashboard',
        component: DashboardView,
        meta: { module: 'dashboard' },
    },
    {
        path: 'payments',
        name: 'payments',
        component: PaymentsView,
        meta: { title: 'Payments', module: 'payments' },
    },
    {
        path: 'payments/:tab(all|deposits|upcoming|low-balance|other)',
        redirect: (to) => {
            const tab = String(to.params.tab);

            return {
                name: 'payments',
                query: tab === 'all' ? {} : { tab },
            };
        },
    },
    // PHASE2
    // {
    //     path: 'accounts',
    //     name: 'accounts',
    //     component: AccountsView,
    //     meta: { title: 'Accounts', module: 'accounts' },
    // },
    // {
    //     path: 'imports',
    //     name: 'imports',
    //     redirect: {
    //         name: 'accounts',
    //         query: { tab: 'imports' },
    //     },
    // },
    // {
    //     path: 'accounts/:id',
    //     name: 'account-details',
    //     component: () => import(/* webpackChunkName: "account-details" */ '@/views/accounts/AccountDetailsView.vue'),
    //     meta: { title: 'Account Details', module: 'accounts' },
    // },
    // {
    //     path: 'negotiations',
    //     name: 'negotiations',
    //     component: () => import(/* webpackChunkName: "negotiations" */ '@/views/negotiations/NegotiationsView.vue'),
    //     meta: { title: 'Negotiations', module: 'negotiations' },
    // },
    // {
    //     path: 'negotiations/:id',
    //     name: 'negotiation-details',
    //     component: () => import(/* webpackChunkName: "negotiation-details" */ '@/views/negotiations/NegotiationDetailsView.vue'),
    //     meta: { title: 'Negotiation Details', module: 'negotiations' },
    // },
    {
        path: 'reports',
        name: 'reports',
        component: ReportsView,
        meta: { title: 'Reports', module: 'reports' },
    },
    {
        path: 'settings',
        name: 'settings',
        component: () => import(/* webpackChunkName: "settings" */ '@/views/settings/SettingsView.vue'),
        meta: { title: 'Settings', module: 'settings' },
    },
    {
        path: 'my-account',
        name: 'my-account',
        component: () => import(/* webpackChunkName: "my-account" */ '@/views/MyAccountView.vue'),
        meta: { title: 'My Account' },
    },
];

const routes: Array<RouteRecordRaw> = [
    {
        path: '/',
        component: AppLayout,
        meta: { requiresCreditor: true },
        children: appShellChildren,
    },
    {
        path: '/superadmin',
        component: () => import(
            /* webpackChunkName: "superadmin-layout" */
            '@/layouts/SuperAdminLayout.vue'
        ),
        children: [
            {
                path: '',
                redirect: { name: 'superadmin-creditors' },
            },
            {
                path: 'creditors',
                name: 'superadmin-creditors',
                component: () => import(
                    /* webpackChunkName: "superadmin-creditors" */
                    '@/views/superadmin/CreditorsView.vue'
                ),
                meta: { title: 'Creditors', module: 'superadmin' },
            },
            {
                path: 'users',
                name: 'superadmin-users',
                component: () => import(
                    /* webpackChunkName: "superadmin-users" */
                    '@/views/superadmin/SuperAdminUsersView.vue'
                ),
                meta: { title: 'Super Admin Users', module: 'superadmin' },
            },
            {
                path: 'my-account',
                name: 'superadmin-my-account',
                component: () => import(
                    /* webpackChunkName: "my-account" */
                    '@/views/MyAccountView.vue'
                ),
                meta: { title: 'My Account', module: 'superadmin' },
            },
        ],
    },
    {
        path: '/sign-in',
        component: AuthLayout,
        props: { showForgotPassword: true },
        children: [
            {
                path: '',
                name: 'sign-in',
                component: () => import(/* webpackChunkName: "sign-in" */ '@/views/SignInView.vue'),
                meta: { title: 'Sign In' },
            },
        ],
    },
    {
        path: '/auto-login',
        component: AuthLayout,
        props: { showForgotPassword: false },
        children: [
            {
                path: '',
                name: 'auto-login',
                component: () => import(/* webpackChunkName: "auto-login" */ '@/views/AutoLoginView.vue'),
                meta: { title: 'Sign In' },
            },
        ],
    },
    {
        path: '/auth/callback',
        component: AuthLayout,
        props: { showForgotPassword: false },
        children: [
            {
                path: '',
                name: 'auth-callback',
                component: () => import(/* webpackChunkName: "auth-callback" */ '@/views/OAuthCallbackView.vue'),
            },
        ],
    },
    {
        path: '/:pathMatch(.*)*',
        redirect: '/',
    },
];

const router = createRouter({
    history: createWebHashHistory(process.env.BASE_URL),
    routes,
});

router.beforeEach(async (to, _from, next) => {
    const isAuthCallbackRoute = to.path === '/auth/callback';
    const isPublic = to.name && publicRouteNames.includes(to.name as string);
    const authStore = useAuthStore();

    if (isAuthCallbackRoute) {
        next();
        return;
    }

    if (!authStore.isAuthenticated && !isPublic) {
        await authStore.refreshUserSession();
    }

    if (isPublic) {
        if (authStore.isAuthenticated) {
            const fallback = pickFallbackRouteName(authStore.roleName) ?? 'dashboard';
            next({ name: fallback });
            return;
        }

        next();
        return;
    }

    if (!authStore.isAuthenticated) {
        next({ name: 'sign-in' });
        return;
    }

    // Superadmin may only enter creditor-scoped routes while impersonating.
    const requiresCreditor = to.matched.some((record) => record.meta.requiresCreditor);
    if (requiresCreditor && authStore.roleName === 'superadmin') {
        const creditorAccessStore = useSuperAdminCreditorAccessStore();

        if (!creditorAccessStore.isActive) {
            await creditorAccessStore.hydrateAccess();
        }

        if (!creditorAccessStore.isActive) {
            next({ name: 'superadmin-creditors' });
            return;
        }
    }

    // Module permission gate
    const requiredModule = to.meta.module as ModuleKey | undefined;
    if (!requiredModule) {
        next();
        return;
    }

    if (authStore.can(requiredModule)) {
        next();
        return;
    }

    // No access, fallback or sign the user out
    const fallback = pickFallbackRouteName(authStore.roleName);

    if (!fallback) {
        // Role is null/unknown – clear session
        authStore.clearSession();
        return;
    }

    next({ name: fallback });
});

router.afterEach((to) => {
    const pageTitle = to.meta?.title as string | undefined;
    document.title = pageTitle ? `${pageTitle} | ${APP_NAME}` : APP_NAME;
});

export default router;
